import type {
  ArtPack,
  ArtPackManifest,
  AuthoredDirectionalAnimTable,
  SpriteTemplate,
} from "../../art/types";
import {
  loadAuthoredDirectionalAnimSet,
  singleFrameAnimSet,
} from "../../art/anim-set";
import affiliation from "../tables/art/packs/dropfront/affiliation.json";
import packJson from "../tables/art/packs/dropfront/pack.json";
import palette from "../tables/art/packs/dropfront/palette.json";
import assault from "../tables/art/packs/dropfront/sprites/assault.json";
import barrier from "../tables/art/packs/dropfront/sprites/barrier.json";
import beacon from "../tables/art/packs/dropfront/sprites/beacon.json";
import depot from "../tables/art/packs/dropfront/sprites/depot.json";
import engineer from "../tables/art/packs/dropfront/sprites/engineer.json";
import hq from "../tables/art/packs/dropfront/sprites/hq.json";
import nest from "../tables/art/packs/dropfront/sprites/nest.json";
import pod from "../tables/art/packs/dropfront/sprites/pod.json";
import shooter from "../tables/art/packs/dropfront/sprites/shooter.json";
import swarm from "../tables/art/packs/dropfront/sprites/swarm.json";
import turret from "../tables/art/packs/dropfront/sprites/turret.json";
import tileFloor from "../tables/art/packs/dropfront/sprites/tile_floor.json";
import tileFloorAlt from "../tables/art/packs/dropfront/sprites/tile_floor_alt.json";
import tileRift from "../tables/art/packs/dropfront/sprites/tile_rift.json";
import tileRiftEdge from "../tables/art/packs/dropfront/sprites/tile_rift_edge.json";
import assaultAnim from "../tables/art/packs/dropfront/anims/assault.json";
import engineerAnim from "../tables/art/packs/dropfront/anims/engineer.json";
import shooterAnim from "../tables/art/packs/dropfront/anims/shooter.json";
import swarmAnim from "../tables/art/packs/dropfront/anims/swarm.json";

const COMBAT_ANIMS: Record<string, AuthoredDirectionalAnimTable> = {
  assault: assaultAnim as AuthoredDirectionalAnimTable,
  shooter: shooterAnim as AuthoredDirectionalAnimTable,
  engineer: engineerAnim as AuthoredDirectionalAnimTable,
  swarm: swarmAnim as AuthoredDirectionalAnimTable,
};

const spriteList = [
  assault,
  shooter,
  engineer,
  barrier,
  swarm,
  nest,
  hq,
  beacon,
  depot,
  pod,
  turret,
  tileFloor,
  tileFloorAlt,
  tileRift,
  tileRiftEdge,
] as SpriteTemplate[];

/**
 * Combat: authored dirs e/n/s (+ W=flip(E) at load). Presentation plays
 * facing × idle/walk/attack/death body clips via ensureAnimFrame; UnitRig owns
 * weapon (+ grip slot). Props: static side.
 */
export function loadDropfrontArtPack(): ArtPack {
  const sprites: Record<string, SpriteTemplate> = {};
  const anims: ArtPack["anims"] = {};
  for (const s of spriteList) {
    sprites[s.id] = s;
    const authored = COMBAT_ANIMS[s.id];
    if (authored) {
      const set = loadAuthoredDirectionalAnimSet(s.id, authored);
      anims[s.id] = set;
      const idle0 = authored.dirs.e.idle.frames[0];
      if (idle0) {
        sprites[s.id] = { id: s.id, size: s.size, rows: idle0 };
      }
    } else {
      anims[s.id] = singleFrameAnimSet(s, 2);
    }
  }
  return {
    manifest: packJson as ArtPackManifest,
    palette,
    affiliation,
    sprites,
    anims,
  };
}
